import { Link, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import AuthPageLayout from './AuthPageLayout'
import Login from './Login'
import SignUp from './SignUp'
import ForgotPassword from './ForgotPassword'

const STEPS = [
  { key: 'login', label: 'Sign in' },
  { key: 'signup', label: 'Sign up' },
  { key: 'forgot-password', label: 'Forgot password' },
] as const

/**
 * Preview-only route for the full-page auth rebuild (`/auth-preview/:step`).
 * Renders the exact same `Login` / `SignUp` / `ForgotPassword` flows the live
 * `/auth?step=` dialog uses — same hooks, validation, and API calls, zero
 * logic changes — just swapped into `AuthPageLayout`'s full-page split
 * layout instead of a modal.
 *
 * Not linked from anywhere yet: the in-form links (e.g. "New here? Create
 * your account") still point at `/login`, `/signup`, `/forgot-password`,
 * which reopen the existing dialog rather than staying inside this preview.
 * That's intentional for now — routing/links get wired up in a follow-up.
 */
export default function AuthPreviewPage() {
  const { step } = useParams<{ step: string }>()

  let Screen = Login
  if (step === 'signup') Screen = SignUp
  else if (step === 'forgot-password') Screen = ForgotPassword

  return (
    <AuthPageLayout>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '6px',
            padding: '10px 0 0',
          }}
        >
          {STEPS.map((s) => (
            <Link
              key={s.key}
              to={`/auth-preview/${s.key}`}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 999,
                textDecoration: 'none',
                color: step === s.key ? '#fff' : 'var(--ds-ink-muted, #4b4b52)',
                background: step === s.key ? 'var(--ds-ink, #0d0d0f)' : 'var(--ds-hover, #f1f1ef)',
              }}
            >
              {s.label}
            </Link>
          ))}
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Screen />
        </Box>
      </Box>
    </AuthPageLayout>
  )
}
